'use client';

import { LogoSmall } from '../../Logo';
import {
  ThriventBankModal,
  showThriventBankModalAtom,
} from '../../ThriventBankModal';
import {
  DisclosureText,
  Disclosures,
  DisclosuresContainer,
  ExtraLinksContainer,
  FullWidthFooterContainer,
  LinkColumnTitle,
  LinksColumn,
  LinksColumnMobile,
  LinksContainer,
  MaxWidthContainer,
  NavigationLink,
  NavigationLinkButton,
  ResourceLink,
  ResourceLinkButton,
  ResourceLinksColumn,
  ResourceLinksContainer,
  SocialLink,
  SocialLinkContainer,
  SocialLinksContainer,
  TitleContainer,
  TitleText,
  TrademarkText,
} from './FooterRedesign.style';
import { footerData } from './data/footer-data';
import { FooterMobileAccordion } from './footer-mobile-accordion/FooterMobileAccordion';
import { Icon } from '@exploration/icons';
import { parseSanitizeItem } from '@exploration/ui-utils';
import { Facebook, Instagram, Linkedin, X, Youtube } from '@midwest/web/icons';
import { MidwestColors, desktopBreakpoint } from '@thrivent/midwest-shared';
import { useAtom } from 'jotai';
import { useMedia } from 'react-use';
import { useTheme } from 'styled-components';

export const FooterRedesign = ({
  showCookieSettings,
}: {
  showCookieSettings: boolean;
}) => {
  const [, setOpen] = useAtom(showThriventBankModalAtom);
  const isMobile = useMedia(`(max-width: ${desktopBreakpoint}px)`);
  const { midwestColors }: { midwestColors: MidwestColors } = useTheme();

  return (
    <>
      <ThriventBankModal />
      <FullWidthFooterContainer>
        <MaxWidthContainer>
          <TitleContainer>
            <LogoSmall />
            <TitleText>
              {footerData.footerText.title}
              <TrademarkText>TM</TrademarkText>
            </TitleText>
          </TitleContainer>
          {!isMobile && (
            <LinksContainer>
              <LinksColumn>
                <LinkColumnTitle data-testid="footer-column-1-title">
                  {footerData.footerText.column1Title}
                </LinkColumnTitle>
                {footerData.column1Links.map((link, index) => (
                  <NavigationLink
                    data-testid={`footer-column-1-link-${index + 1}`}
                    key={link.label}
                    href={link.url}
                  >
                    {link.label}
                  </NavigationLink>
                ))}
              </LinksColumn>
              <LinksColumn>
                <LinkColumnTitle data-testid="footer-column-2-title">
                  {footerData.footerText.column2Title}
                </LinkColumnTitle>
                {footerData.column2Links.map((link, index) => (
                  <NavigationLink
                    data-testid={`footer-column-2-link-${index + 1}`}
                    key={link.label}
                    href={link.url}
                  >
                    {link.label}
                  </NavigationLink>
                ))}
              </LinksColumn>
              <LinksColumn>
                <LinkColumnTitle data-testid="footer-column-3-title">
                  {footerData.footerText.column3Title}
                </LinkColumnTitle>
                {footerData.column3Links.map((link, index) => (
                  <NavigationLink
                    data-testid={`footer-column-3-link-${index + 1}`}
                    key={link.label}
                    href={link.url}
                  >
                    {link.label}
                  </NavigationLink>
                ))}
              </LinksColumn>
              <LinksColumn>
                <LinkColumnTitle data-testid="footer-column-4-title">
                  {footerData.footerText.column4Title}
                </LinkColumnTitle>
                <NavigationLinkButton onClick={() => setOpen(true)}>
                  Thrivent Bank
                </NavigationLinkButton>
                {footerData.column4Links.map((link, index) => (
                  <NavigationLink
                    data-testid={`footer-column-4-link-${index + 1}`}
                    key={link.label}
                    href={link.url}
                  >
                    {link.label}
                  </NavigationLink>
                ))}
              </LinksColumn>
            </LinksContainer>
          )}
          {isMobile && (
            <LinksContainer>
              <LinksColumnMobile>
                <FooterMobileAccordion
                  title={footerData.footerText.column1Title}
                >
                  <LinksColumn>
                    {footerData.column1Links.map((link) => (
                      <NavigationLink key={link.label} href={link.url}>
                        {link.label}
                      </NavigationLink>
                    ))}
                  </LinksColumn>
                </FooterMobileAccordion>
                <FooterMobileAccordion
                  title={footerData.footerText.column2Title}
                >
                  <LinksColumn>
                    {footerData.column2Links.map((link) => (
                      <NavigationLink key={link.label} href={link.url}>
                        {link.label}
                      </NavigationLink>
                    ))}
                  </LinksColumn>
                </FooterMobileAccordion>
                <FooterMobileAccordion
                  title={footerData.footerText.column3Title}
                >
                  <LinksColumn>
                    {footerData.column3Links.map((link) => (
                      <NavigationLink key={link.label} href={link.url}>
                        {link.label}
                      </NavigationLink>
                    ))}
                  </LinksColumn>
                </FooterMobileAccordion>
                <FooterMobileAccordion
                  title={footerData.footerText.column4Title}
                >
                  <LinksColumn>
                    <NavigationLinkButton onClick={() => setOpen(true)}>
                      Thrivent Bank
                    </NavigationLinkButton>
                    {footerData.column4Links.map((link) => (
                      <NavigationLink key={link.label} href={link.url}>
                        {link.label}
                      </NavigationLink>
                    ))}
                  </LinksColumn>
                </FooterMobileAccordion>
              </LinksColumnMobile>
            </LinksContainer>
          )}
          <DisclosuresContainer>
            <ExtraLinksContainer>
              <ResourceLinksContainer>
                <ResourceLinksColumn>
                  {footerData.resourceLinks.slice(0, 3).map((link) => (
                    <ResourceLink key={link.label} href={link.url}>
                      {link.label}
                    </ResourceLink>
                  ))}
                </ResourceLinksColumn>
                <ResourceLinksColumn>
                  {footerData.resourceLinks.slice(3, 5).map((link) => (
                    <ResourceLink key={link.label} href={link.url}>
                      {link.label}
                    </ResourceLink>
                  ))}
                  {showCookieSettings && (
                    <ResourceLinkButton
                      onClick={() => window?.OneTrust.ToggleInfoDisplay()}
                      aria-label="cookie settings"
                    >
                      Cookie Settings
                    </ResourceLinkButton>
                  )}
                </ResourceLinksColumn>
              </ResourceLinksContainer>
              <SocialLinksContainer>
                <SocialLinkContainer>
                  <SocialLink
                    href={footerData.socialLinks[0].url}
                    target="_blank"
                    aria-label="facebook - opens in new tab"
                    data-testid="facebook"
                  >
                    <Facebook
                      fill={
                        midwestColors.componentButtonColorBackgroundStrongPrimaryPressed
                      }
                      width={16}
                      height={16}
                      role="img"
                      aria-hidden
                    />
                  </SocialLink>
                </SocialLinkContainer>
                <SocialLinkContainer>
                  <SocialLink
                    href={footerData.socialLinks[1].url}
                    target="_blank"
                    aria-label="linkedin - opens in new tab"
                    data-testid="linkedIn"
                  >
                    <Linkedin
                      fill={
                        midwestColors.componentButtonColorBackgroundStrongPrimaryPressed
                      }
                      width={16}
                      height={16}
                      role="img"
                      aria-hidden
                    />
                  </SocialLink>
                </SocialLinkContainer>
                <SocialLinkContainer>
                  <SocialLink
                    href={footerData.socialLinks[2].url}
                    target="_blank"
                    aria-label="x.com - opens in new tab"
                    data-testid="x.com"
                  >
                    <X
                      fill={
                        midwestColors.componentButtonColorBackgroundStrongPrimaryPressed
                      }
                      width={16}
                      height={16}
                      role="img"
                      aria-hidden
                    />
                  </SocialLink>
                </SocialLinkContainer>
                <SocialLinkContainer>
                  <SocialLink
                    href={footerData.socialLinks[3].url}
                    target="_blank"
                    aria-label="instagram - opens in new tab"
                    data-testid="instagram"
                  >
                    <Instagram
                      fill={
                        midwestColors.componentButtonColorBackgroundStrongPrimaryPressed
                      }
                      width={16}
                      height={16}
                      role="img"
                      aria-hidden
                    />
                  </SocialLink>
                </SocialLinkContainer>
                <SocialLinkContainer>
                  <SocialLink
                    href={footerData.socialLinks[4].url}
                    target="_blank"
                    aria-label="Youtube - opens in new tab"
                    data-testid="youtube"
                  >
                    <Youtube
                      fill={
                        midwestColors.componentButtonColorBackgroundStrongPrimaryPressed
                      }
                      width={16}
                      height={16}
                      role="img"
                      aria-hidden
                    />
                  </SocialLink>
                </SocialLinkContainer>
              </SocialLinksContainer>
            </ExtraLinksContainer>
            {!!footerData?.disclosures && (
              <Disclosures>
                {footerData.disclosures.map((disclosure) => (
                  <DisclosureText key={disclosure.key}>
                    {parseSanitizeItem(disclosure.text)}
                  </DisclosureText>
                ))}
              </Disclosures>
            )}
            <br />
            <Icon icon={footerData?.footerText?.brokerCheckSVG} />
            <DisclosureText>
              {parseSanitizeItem(footerData?.footerText.brokerCheckText)}
            </DisclosureText>
          </DisclosuresContainer>
        </MaxWidthContainer>
      </FullWidthFooterContainer>
    </>
  );
};
